import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, CheckCircle2, Users, Heart, Calendar, Building2, Sparkles, MessageSquare, X, Plus, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { fmtUSD, FLAGS, fmtDate } from "@/lib/utils";
import DevStatusBadge from "@/components/DevStatusBadge";
import VerifiedBadges from "@/components/VerifiedBadges";
import { SecureImage } from "@/components/ImageUploader";

export default function DevelopmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dev, setDev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("units");
  const [activeImg, setActiveImg] = useState(0);
  const [showInterest, setShowInterest] = useState(false);
  const [interestUnit, setInterestUnit] = useState(null);
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [showPostUpdate, setShowPostUpdate] = useState(false);

  async function reload() {
    const { data } = await api.get(`/developments/${id}`);
    setDev(data);
  }

  const isOwner = user && dev && user.id === dev.developer_id;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/developments/${id}`);
        setDev(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto py-20 text-center text-neutral-500">Loading...</div>;
  if (!dev) return <div className="max-w-7xl mx-auto py-20 text-center text-neutral-500">Development not found</div>;

  async function submitInterest() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/interests", {
        development_id: dev.id,
        unit_id: interestUnit?.id,
        message: msg,
      });
      toast.success("Interest submitted — we've opened a chat with the developer.");
      setShowInterest(false);
      setMsg("");
      if (data?.conversation_id) {
        navigate(`/messages?c=${data.conversation_id}`);
      } else {
        navigate("/messages");
      }
    } catch (e) {
      toast.error("Could not submit. Try again.");
      setSuccess("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function messageDeveloper() {
    try {
      await api.post("/messages", {
        development_id: dev.id,
        text: `Hi, I'm interested in ${dev.name}.`,
      });
      navigate("/messages");
    } catch { /* ignore */ }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <Link to="/discover" data-testid="back-link" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 mb-6">
        <ArrowLeft size={14} /> Back to Discover
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Gallery + Info */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl overflow-hidden aspect-[16/10] bg-neutral-100 relative">
            <SecureImage src={dev.gallery?.[activeImg] || dev.cover_image} alt={dev.name} className="w-full h-full object-cover" />
            <div className="absolute top-4 left-4 right-4 flex gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 backdrop-blur text-neutral-900">{FLAGS[dev.country]} {dev.country}</span>
              <VerifiedBadges dev={dev} variant="overlay" />
            </div>
            <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
              {activeImg + 1} / {(dev.gallery || []).length || 1}
            </div>
          </div>
          {dev.gallery?.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {dev.gallery.map((src, i) => (
                <button
                  key={i}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`rounded-xl overflow-hidden aspect-[4/3] border-2 transition ${i === activeImg ? "border-propera" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <SecureImage src={src} className="w-full h-full object-cover" alt={`view ${i + 1}`} />
                </button>
              ))}
            </div>
          )}

          {/* About */}
          <section className="mt-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">{dev.name}</h2>
            <p className="text-sm text-neutral-500 flex items-center gap-1 mt-1 flex-wrap"><MapPin size={13} /> {dev.city}, {dev.country} · by <span className="text-propera font-medium ml-1">{dev.developer_name}</span></p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <DevStatusBadge status={dev.status || "coming_soon"} />
              {(dev.tags || []).map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">{t}</span>
              ))}
            </div>
            <p className="mt-6 text-neutral-700 leading-relaxed">{dev.description}</p>
          </section>

          {/* Key selling points */}
          <section className="mt-8">
            <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-2"><Sparkles size={12} /> Key Selling Points</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {(dev.selling_points || []).map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-propera-light/50">
                  <CheckCircle2 size={18} className="text-propera flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-800">{p}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Amenities */}
          <section className="mt-8">
            <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Amenities ({(dev.amenities || []).length})</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(dev.amenities || []).map((a) => (
                <span key={a} className="px-4 py-2 rounded-full bg-white border border-neutral-200 text-sm text-neutral-700">{a}</span>
              ))}
            </div>
          </section>

          {/* Tabs */}
          <section className="mt-10">
            <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto no-scrollbar">
              {[
                { k: "units", label: `Units (${dev.units?.length || 0})` },
                { k: "updates", label: `Updates (${dev.updates?.length || 0})` },
              ].map((t) => (
                <button
                  key={t.k}
                  data-testid={`tab-${t.k}`}
                  onClick={() => setTab(t.k)}
                  className={`px-4 py-3 text-sm font-semibold transition border-b-2 ${
                    tab === t.k ? "text-propera border-propera" : "text-neutral-500 border-transparent hover:text-neutral-900"
                  }`}
                >{t.label}</button>
              ))}
            </div>

            {tab === "units" && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {(dev.units || []).sort((a,b) => (a.status === "available" ? -1 : 1)).map((u) => (
                  <UnitCard key={u.id} unit={u} onInterest={() => { setInterestUnit(u); setShowInterest(true); }} />
                ))}
              </div>
            )}
            {tab === "updates" && (
              <div className="mt-6 space-y-4">
                {isOwner && (
                  <button
                    data-testid="post-update-btn"
                    onClick={() => setShowPostUpdate(true)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm"
                  >
                    <Plus size={14} /> Post Update
                  </button>
                )}
                {(dev.updates || []).map((u) => {
                  const units = u.units || [];
                  return (
                    <div key={u.id} className="soft-card p-5" data-testid={`update-${u.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">{fmtDate(u.created_at)}</p>
                        {units.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {units.map((un) => (
                              <span key={un.id} className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-propera-light text-propera">
                                {un.label} · {un.bedrooms}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-neutral-100 text-neutral-700">
                            Development-wide
                          </span>
                        )}
                      </div>
                      <p className="text-base font-semibold text-neutral-900 mt-2">{u.title}</p>
                      <p className="text-sm text-neutral-600 mt-1 whitespace-pre-line">{u.body}</p>
                    </div>
                  );
                })}
                {(!dev.updates || dev.updates.length === 0) && !isOwner && (
                  <p className="text-center text-sm text-neutral-500 py-10">No updates yet</p>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Right sticky panel */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24">
            <div className="soft-card p-6">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Starting from</p>
              <p className="text-3xl sm:text-4xl font-bold text-neutral-900 tabular mt-1 break-words">{fmtUSD(dev.starting_price)}</p>

              <div className="grid grid-cols-3 gap-3 mt-6">
                <Stat label="Available" value={dev.available_units} />
                <Stat label="Total units" value={dev.total_units} />
                <Stat label="Interested" value={dev.interest_count} />
              </div>

              {dev.payment_plan && (
                <div className="mt-6 rounded-2xl p-4 bg-propera-light/50">
                  <p className="text-xs uppercase tracking-wider text-propera font-semibold mb-2">Payment Plan</p>
                  <div className="flex items-center gap-2 text-sm">
                    <PlanStep pct={dev.payment_plan.booking} label="Booking" />
                    <PlanStep pct={dev.payment_plan.construction} label="Construction" />
                    <PlanStep pct={dev.payment_plan.handover} label="Handover" />
                  </div>
                </div>
              )}

              {dev.completion_date && (
                <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500"><Calendar size={12} /> Expected completion {fmtDate(dev.completion_date, { month: "long" })}</div>
              )}

              {user?.role === "investor" && (
                <>
                  <button
                    data-testid="express-interest-btn"
                    onClick={() => { setInterestUnit(null); setShowInterest(true); }}
                    className="mt-6 w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3.5 font-semibold transition glow-primary flex items-center justify-center gap-2"
                  >
                    <Heart size={16} /> Express Interest
                  </button>
                  <button
                    data-testid="message-developer-btn"
                    onClick={messageDeveloper}
                    className="mt-2 w-full bg-white border border-neutral-200 text-neutral-900 rounded-full px-6 py-3 font-semibold hover:bg-neutral-50 transition flex items-center justify-center gap-2"
                  >
                    <MessageSquare size={16} /> Message Developer
                  </button>
                </>
              )}
            </div>

            <div className="soft-card p-6 mt-4">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">Location</p>
              <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-gradient-to-br from-propera-light to-neutral-100 flex items-center justify-center text-propera">
                <MapPin size={28} />
              </div>
              <p className="mt-3 text-sm text-neutral-700"><b>{dev.city}</b>, {dev.country}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Interest Modal */}
      {showInterest && (
        <Modal onClose={() => setShowInterest(false)} testId="interest-modal">
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-propera-light text-propera flex items-center justify-center mx-auto"><Heart size={22} /></div>
            <h3 className="text-2xl font-bold text-neutral-900 mt-4">Express Interest</h3>
            <p className="text-sm text-neutral-500 mt-1">{interestUnit ? `${interestUnit.label} · ${interestUnit.bedrooms} · ${fmtUSD(interestUnit.price)}` : dev.name}</p>
          </div>

          <div className="mt-5 rounded-2xl bg-neutral-50 border border-neutral-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">We&apos;ll open a chat and send this message</p>
            <p className="text-sm text-neutral-800 mt-1.5 leading-relaxed">
              Hi, I&apos;m interested in <span className="font-semibold">{dev.name}</span>
              {interestUnit ? <span> (<span className="font-semibold">{interestUnit.label} · {interestUnit.bedrooms}</span>)</span> : null}
              . Please share more details.
            </p>
          </div>

          <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-5 mb-2">Add a personal note (optional)</label>
          <textarea
            data-testid="interest-message"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="e.g. I'd like a virtual tour and payment plan details."
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition text-sm"
          />
          {success && <p className="text-sm text-red-500 text-center mt-2 font-medium">{success}</p>}
          <button
            data-testid="submit-interest"
            onClick={submitInterest}
            disabled={submitting}
            className="w-full mt-4 bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Submit & Open Chat"}
          </button>
        </Modal>
      )}

      {/* Post Update Modal (developer only) */}
      {showPostUpdate && isOwner && (
        <PostUpdateModal
          dev={dev}
          onClose={() => setShowPostUpdate(false)}
          onPosted={async () => { setShowPostUpdate(false); await reload(); }}
        />
      )}
    </div>
  );
}

function PostUpdateModal({ dev, onClose, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState("all"); // 'all' | 'units'
  const [unitIds, setUnitIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  function toggleUnit(id) {
    setUnitIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  }

  async function submit() {
    if (title.trim().length < 3) {
      toast.error("Title too short");
      return;
    }
    if (body.trim().length < 5) {
      toast.error("Add more detail to the update body");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/developments/${dev.id}/updates`, {
        title: title.trim(),
        body: body.trim(),
        unit_ids: scope === "units" ? unitIds : [],
      });
      toast.success("Update posted — investors have been notified");
      onPosted();
    } catch (e) {
      toast.error("Could not post update");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} testId="post-update-modal">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-propera-light text-propera flex items-center justify-center flex-shrink-0"><Megaphone size={20} /></div>
        <div>
          <h3 className="text-xl font-bold text-neutral-900">Post an update</h3>
          <p className="text-xs text-neutral-500">Notify investors watching {dev.name}</p>
        </div>
      </div>

      <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-5 mb-1.5">Title</label>
      <input
        data-testid="update-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Foundation works complete on Block A"
        className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm"
      />

      <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-4 mb-1.5">Details</label>
      <textarea
        data-testid="update-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Share the milestone, timeline, or next steps investors should know…"
        rows={4}
        className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm"
      />

      <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-5 mb-2">Applies to</label>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="scope-all"
          onClick={() => setScope("all")}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${scope === "all" ? "bg-propera text-white border-propera" : "bg-white border-neutral-200 text-neutral-700"}`}
        >Entire development</button>
        <button
          type="button"
          data-testid="scope-units"
          onClick={() => setScope("units")}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${scope === "units" ? "bg-propera text-white border-propera" : "bg-white border-neutral-200 text-neutral-700"}`}
        >Specific unit(s)</button>
      </div>

      {scope === "units" && (
        <div className="mt-3 max-h-52 overflow-y-auto rounded-xl border border-neutral-200 divide-y divide-neutral-100">
          {(dev.units || []).length === 0 ? (
            <p className="text-sm text-neutral-500 p-4 text-center">No units added yet</p>
          ) : (
            (dev.units || []).map((u) => (
              <label key={u.id} data-testid={`unit-select-${u.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={unitIds.includes(u.id)}
                  onChange={() => toggleUnit(u.id)}
                  className="accent-propera w-4 h-4"
                />
                <span className="text-sm text-neutral-800 flex-1"><span className="font-semibold">{u.label}</span> · {u.bedrooms}</span>
                <span className="text-xs text-neutral-500 tabular">{fmtUSD(u.price)}</span>
              </label>
            ))
          )}
        </div>
      )}

      <button
        data-testid="submit-update"
        onClick={submit}
        disabled={submitting || (scope === "units" && unitIds.length === 0)}
        className="w-full mt-5 bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition disabled:opacity-60"
      >
        {submitting ? "Posting…" : "Post Update"}
      </button>
    </Modal>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-bold tabular text-neutral-900">{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">{label}</p>
    </div>
  );
}

function PlanStep({ pct, label }) {
  return (
    <div className="flex-1 text-center">
      <p className="text-lg font-bold text-propera tabular">{pct}%</p>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p>
    </div>
  );
}

function UnitCard({ unit, onInterest }) {
  const { user, refreshUser } = useAuth();
  const isSaved = (user?.watchlist || []).includes(unit.id);
  async function toggleSave(e) {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/watchlist/toggle/${unit.id}`);
      await refreshUser();
    } catch { /* ignore */ }
  }
  const statusStyle = {
    available: "bg-propera-light text-propera",
    reserved: "bg-amber-50 text-amber-600",
    pending: "bg-blue-50 text-blue-600",
    sold: "bg-neutral-100 text-neutral-500",
  }[unit.status];
  return (
    <div className="soft-card p-5 relative">
      {user?.role === "investor" && (
        <button
          type="button"
          data-testid={`unit-save-${unit.id}`}
          onClick={toggleSave}
          aria-label={isSaved ? "Remove from watchlist" : "Add to watchlist"}
          className={`absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition ${isSaved ? "bg-propera text-white" : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"}`}
        >
          <Heart size={16} fill={isSaved ? "currentColor" : "none"} />
        </button>
      )}
      <div className="flex items-start justify-between pr-12">
        <div>
          <p className="font-semibold text-neutral-900">{unit.label}</p>
          <p className="text-xs text-neutral-500">Floor {unit.floor} · {unit.bedrooms}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${statusStyle}`}>{unit.status}</span>
      </div>
      <p className="text-2xl font-bold text-neutral-900 tabular mt-3">{fmtUSD(unit.price)}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-neutral-400 text-[10px] uppercase tracking-wider">Deposit</p>
          <p className="font-semibold text-neutral-900 tabular">{fmtUSD(unit.deposit)}</p>
        </div>
        <div>
          <p className="text-neutral-400 text-[10px] uppercase tracking-wider">Est. Rent</p>
          <p className="font-semibold text-neutral-900 tabular">{fmtUSD(unit.estimated_rent)}/mo</p>
        </div>
      </div>
      {unit.status === "available" && (
        <button
          data-testid={`unit-interest-${unit.id}`}
          onClick={onInterest}
          className="mt-4 w-full bg-propera hover:bg-propera-hover text-white rounded-full px-4 py-2 text-sm font-semibold transition"
        >
          Express Interest
        </button>
      )}
    </div>
  );
}

function Modal({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" data-testid={testId} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-8 max-w-md w-full relative animate-fade-up">
        <button data-testid="modal-close" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-neutral-100"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}
